import { readableColor } from 'polished';
import { useEffect } from 'react';

/**
 * Bridges the building's brand colour to the CSS variables `@hqo/ui-atoms` reads
 * (`--primary`, `--ring`), so shared-package components match the whitelabel the
 * RCL `HqoThemeProvider` already gets. Without a brand colour the
 * `@hqo/tailwind-theme` defaults stay in place.
 */
export const BRAND_CSS_VARIABLES = ['--primary', '--ring'] as const;

/**
 * `@hqo/tailwind-theme` pairs `--primary` with a fixed white `--primary-foreground`, so a light
 * brand colour would put white text on a light button.
 *
 * ⭐ THE WHITELABEL'S OWN `primary_font_color` WINS. It is the colour the brand chose to sit ON
 * its primary, and tenant-portal maps exactly that field to `--primary-foreground` (see
 * `shared/contexts/building-theme`). Deriving one here instead made this app disagree with the
 * portal it mounts inside -- and worse, win the disagreement: this hook writes an INLINE style on
 * the root element, which outranks the `:root` rule the portal's provider appends.
 *
 * ⚠️ The derived pairing is the FALLBACK, for a brand that configured no font colour.
 * `readableColor` flips on luminance alone, so a mid-tone brand (HqO's own orange among them) comes
 * back black -- readable, and not what any HqO primary button looks like anywhere else.
 */
const PRIMARY_FOREGROUND_VARIABLE = '--primary-foreground';

export const useBrandCssVariables = (primaryColor?: string | null, primaryFontColor?: string | null): void => {
  useEffect(() => {
    if (!primaryColor) {
      return undefined;
    }

    const root = document.documentElement;
    BRAND_CSS_VARIABLES.forEach(variable => root.style.setProperty(variable, primaryColor));

    if (primaryFontColor) {
      root.style.setProperty(PRIMARY_FOREGROUND_VARIABLE, primaryFontColor);
    } else {
      // `readableColor` throws on a colour it cannot parse; the theme's own pairing is the fallback.
      try {
        root.style.setProperty(PRIMARY_FOREGROUND_VARIABLE, readableColor(primaryColor, '#000000', '#FFFFFF'));
      } catch {
        root.style.removeProperty(PRIMARY_FOREGROUND_VARIABLE);
      }
    }

    return () => {
      BRAND_CSS_VARIABLES.forEach(variable => root.style.removeProperty(variable));
      root.style.removeProperty(PRIMARY_FOREGROUND_VARIABLE);
    };
  }, [primaryColor, primaryFontColor]);
};
